import React, { useState, forwardRef } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarIcon } from 'lucide-react';

export type FilterType = 'daily' | 'weekly' | 'monthly';

interface DateFilterProps {
  onFilterChange?: (filterType: FilterType, date: Date | null) => void;
}

export function DateFilter({ onFilterChange }: DateFilterProps) {
  const [filterType, setFilterType] = useState<FilterType>('daily');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleFilterTypeChange = (type: FilterType) => {
    setFilterType(type);
    setSelectedDate(null);
    onFilterChange?.(type, null);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setIsCalendarOpen(false);
      onFilterChange?.(filterType, date);
    }
  };

  const formatDisplayDate = () => {
    if (!selectedDate) return 'Select date';
    
    switch (filterType) {
      case 'daily':
        return selectedDate.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      case 'weekly':
        const weekStart = new Date(selectedDate);
        weekStart.setDate(selectedDate.getDate() - selectedDate.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      case 'monthly':
        return selectedDate.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long' 
        });
      default:
        return 'Select date';
    }
  };

  return (
    <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700">Filter by:</span>
        <Select value={filterType} onValueChange={handleFilterTypeChange}>
          <SelectTrigger className="w-32 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="justify-start text-left bg-white hover:bg-gray-50"
            type="button"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formatDisplayDate()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate || undefined}
            onSelect={handleDateSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      {selectedDate && (
        <Button
          variant="ghost"
          onClick={() => {
            setSelectedDate(null);
            onFilterChange?.(filterType, null);
          }}
          className="text-gray-500 hover:text-gray-700"
          type="button"
        >
          Clear
        </Button>
      )}
    </div>
  );
}